import {
  ClipboardDocumentIcon,
  KeyIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { useState } from "react";
import type { FormEvent } from "react";

import {
  useCreateIntegrationToken,
  useIntegrationTokens,
  useRevokeIntegrationToken,
} from "@api/queries";
import { toast } from "@components/hot-toast";
import Toast from "@components/notifications/Toast";
import type { CreatedIntegrationToken } from "@/types";

function formatDate(value: string | null): string {
  return value ? new Date(value).toLocaleString() : "Never";
}

export function IntegrationTokens() {
  const [name, setName] = useState("");
  const [createdToken, setCreatedToken] = useState<CreatedIntegrationToken | null>(null);
  const { data: tokens = [], isLoading } = useIntegrationTokens();
  const createToken = useCreateIntegrationToken();
  const revokeToken = useRevokeIntegrationToken();

  const handleCreate = async (event: FormEvent) => {
    event.preventDefault();
    const trimmedName = name.trim();
    if (!trimmedName) return;

    try {
      const created = await createToken.mutateAsync(trimmedName);
      setCreatedToken(created);
      setName("");
      toast.custom((t) => <Toast type="success" body="Integration token created" t={t} />);
    } catch (error) {
      toast.custom((t) => <Toast type="error" body={(error as Error).message} t={t} />);
    }
  };

  const handleCopy = async () => {
    if (!createdToken) return;

    try {
      await navigator.clipboard.writeText(createdToken.token);
      toast.custom((t) => <Toast type="success" body="Integration token copied" t={t} />);
    } catch {
      toast.custom((t) => <Toast type="error" body="Failed to copy integration token" t={t} />);
    }
  };

  const handleRevoke = async (id: string, tokenName: string) => {
    if (!window.confirm(`Revoke ${tokenName}? Existing workflows will stop working.`)) return;

    try {
      await revokeToken.mutateAsync(id);
      toast.custom((t) => <Toast type="success" body="Integration token revoked" t={t} />);
    } catch (error) {
      toast.custom((t) => <Toast type="error" body={(error as Error).message} t={t} />);
    }
  };

  return (
    <section className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
      <div className="flex items-center gap-2">
        <KeyIcon className="h-5 w-5 text-gray-500" aria-hidden="true" />
        <h3 className="text-sm font-medium text-gray-900 dark:text-white">
          Integration tokens
        </h3>
      </div>
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
        Create revocable credentials for read-only top-stories and feed exports.
      </p>

      <form onSubmit={handleCreate} className="mt-4 flex gap-2">
        <label htmlFor="integration-token-name" className="sr-only">
          Token name
        </label>
        <input
          id="integration-token-name"
          value={name}
          maxLength={100}
          onChange={(event) => setName(event.target.value)}
          placeholder="n8n crypto workflow"
          className="grow rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm dark:text-white"
        />
        <button
          type="submit"
          disabled={!name.trim() || createToken.isPending}
          className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
        >
          Create
        </button>
      </form>

      {createdToken && (
        <div className="mt-4 rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 p-4">
          <p className="text-sm font-medium text-amber-900 dark:text-amber-200">
            Copy this token now. It will not be shown again.
          </p>
          <div className="mt-2 flex gap-2">
            <input
              readOnly
              value={createdToken.token}
              onClick={(event) => event.currentTarget.select()}
              className="grow rounded-md border border-amber-300 bg-white dark:bg-gray-900 px-3 py-2 font-mono text-xs dark:text-white"
            />
            <button
              type="button"
              onClick={handleCopy}
              className="rounded-md border border-amber-400 px-3 py-2 hover:bg-amber-100 dark:hover:bg-amber-900/40"
            >
              <ClipboardDocumentIcon className="h-5 w-5" aria-hidden="true" />
              <span className="sr-only">Copy token</span>
            </button>
          </div>
        </div>
      )}

      <div className="mt-5 space-y-2">
        {isLoading && <p className="text-sm text-gray-500">Loading tokens…</p>}
        {!isLoading && tokens.length === 0 && (
          <p className="text-sm text-gray-500">No integration tokens.</p>
        )}
        {tokens.map((token) => (
          <div
            key={token.id}
            className="flex items-center justify-between gap-4 rounded-md border border-gray-200 dark:border-gray-700 p-3"
          >
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="truncate text-sm font-medium text-gray-900 dark:text-white">
                  {token.name}
                </span>
                <span className="text-xs text-gray-500">
                  {token.revoked_at ? "Revoked" : "Active"}
                </span>
              </div>
              <p className="mt-1 font-mono text-xs text-gray-500">{token.token_prefix}…</p>
              <p className="mt-1 text-xs text-gray-500">
                Created {formatDate(token.created_at)} · Last used {formatDate(token.last_used_at)}
              </p>
            </div>
            {!token.revoked_at && (
              <button
                type="button"
                disabled={revokeToken.isPending}
                onClick={() => handleRevoke(token.id, token.name)}
                className="rounded-md p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 disabled:opacity-50"
              >
                <TrashIcon className="h-5 w-5" aria-hidden="true" />
                <span className="sr-only">Revoke {token.name}</span>
              </button>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
